'use client';

/**
 * Long-form welcome sections — the scrollytelling below the app-wall hero.
 * Rendered only when `AppWallSplash` gets the `longForm` prop (the `/welcome`
 * preview route); the production single-screen splash is untouched.
 *
 * The 4 "beats" (Pro apps / Customize / Collaborate / Earn) are data + a live
 * CSS-motion visual each; `renderBeat(beat, i, variant)` lays them out in one of
 * 10 bold layouts switched live with the q-row keys (q..p → 0..9). Temporary
 * experiment scaffolding. Copy rule: zero em-dashes.
 */

import { useEffect, useId, useRef, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import { Sparkles, Moon, Sun, MousePointer2, Play, Pause, RotateCcw, Check, Zap, Camera } from 'lucide-react';

const FEED: { initial: string; color: string; name: string; by: string; badge: string }[] = [
  { initial: 'V', color: '#7f77dd', name: 'Vector Design', by: 'remixed 1,240 times', badge: 'remix' },
  { initial: 'P', color: '#3b82f6', name: 'Procurement Agent', by: 'by @robutler', badge: 'earning' },
  { initial: 'S', color: '#1d9e75', name: 'Snake Game', by: '420 plays today', badge: 'popular' },
];

/**
 * The questions that stop someone from jumping in, with short answers. The
 * full set lives in the docs (docs/public/guides/faq.md).
 *
 * The ORDER is the ranking: the page shows the first FAQ_TOP rows and folds
 * the rest under "See more FAQs" (FaqSection, below), so promoting a question
 * is moving its line above the fold and nothing else.
 *
 * Three things these answers must keep saying the way they do, because each
 * one is a promise the product has to be able to keep:
 *   - what you earn is Creator Rewards: a balance of its own that can pay for
 *     your own AI work, and the only one that will be cashed out. Cash-out is
 *     "coming soon" (the owner's wording, 2026-10-01; it replaced "isn't open
 *     yet", which read as pessimistic). Credits are never earned, never cashed
 *     out and never move between people (CLAUDE.md, credits rules 2 and 3);
 *   - an agent spends within the limits its owner set, and Robutler bills that
 *     usage. Agents do not negotiate with or pay each other;
 *   - work is private until it is shared or published, but a profile is
 *     public, so "everything is private" is not true.
 * No prices, no plan numbers and no vendor names here: those change, and this
 * page is the one a visitor reads before they have an account.
 */
const FAQ: { q: string; a: string; link?: { text: string; href: string } }[] = [
  { q: 'What is Robutler?', a: 'A free platform where you use pro-grade apps, remix them with your AI, and can earn Creator Rewards when others use what you make.' },
  { q: 'Is it really free?', a: 'Yes. Robutler is free, and every app on it is free to use. AI work, like asking an agent to remix an app, uses credits. You set the limits. Want more? Premium adds a monthly batch of credits, more agents, and more storage.' },
  { q: 'Do I need to sign up?', a: 'Not to try an app. Click Open app and you start as a guest right away. Sign up free to keep your work.' },
  { q: 'Do I need a credit card?', a: 'Not to use the apps. You only need one to buy credits or a plan.' },
  { q: 'Do I need to know how to code, or understand AI?', a: 'No. If you can say what you want in plain words, you can use Robutler.' },
  { q: "What's an agent, and do I need one?", a: "An agent is an AI that does things for you, not just talks. You don't need one to use an app. Every account comes with one, your Robutler, for when you want help." },
  { q: 'Can I use the AI I already have?', a: 'Yes. Connect the AI assistant or coding agent you already use, and it can work in Robutler apps for you.', link: { text: 'How to connect your AI', href: '/docs/guides/connect-ai-app' } },
  { q: 'Will my agent spend without asking?', a: 'Only within the limits you set. Small requests go through on their own, bigger ones wait for your OK, and you can cap spending per request and per day.' },
  { q: 'Is my work private?', a: 'Your work stays private until you share or publish it. Your profile is public.' },
  { q: 'Who owns what I make?', a: 'You do. Apps, agents, videos, docs, designs. All yours.' },
  { q: 'Can I earn?', a: 'Yes. When people use something you publish, you can earn Creator Rewards. They can pay for your own AI work, and cash-out is coming soon.', link: { text: 'How earning works', href: '/docs/guides/earn' } },
];

/** How many questions show before the fold. Eleven rows were too long for the
 *  foot of a landing page (the owner, 2026-10-04). The five kept are the "can I
 *  just start" ones: what it is, the price, signing up, a card, coding. */
const FAQ_TOP = 5;

type Beat = { eyebrow: string; title: string; sub: string; visual: React.ReactNode; narrowTitle?: boolean };

const H2 = 'font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl';
const SUB = 'text-base leading-relaxed text-foreground/70 sm:text-lg';
const EB = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground';

/**
 * Gate a beat's CSS-motion visual so it only animates once it scrolls into
 * view. Best-practice scrollytelling behavior, driven by two IntersectionObservers
 * (performant, no scroll handlers):
 *   • PLAY while the visual sits in a centered band of the viewport, so scenes
 *     fire one at a time as they reach the eye, not all at once.
 *   • PAUSE (freeze the current frame) once it leaves that band but is still
 *     partly on screen.
 *   • RESET to frame 0 only when it scrolls fully off screen, so the next
 *     entry, from either direction, opens cleanly from the beginning.
 */
function AnimateInView({ children }: { children: React.ReactNode }) {
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);
  const [live, setLive] = useState(false);
  const [paused, setPaused] = useState(false);
  const [hasAnim, setHasAnim] = useState(false);
  const rewind = () => {
    const el = ref.current;
    if (el && typeof el.getAnimations === 'function') {
      el.getAnimations({ subtree: true }).forEach((a) => {
        try {
          a.currentTime = 0;
        } catch {
          /* ignore */
        }
      });
    }
  };
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof el.getAnimations === 'function') setHasAnim(el.getAnimations({ subtree: true }).length > 0);
    if (typeof IntersectionObserver === 'undefined') {
      setLive(true);
      return;
    }
    // Play whenever the scene sits in the middle ~60% of the viewport. A
    // tighter band (e.g. -35%) waits for dead-centre, which — combined with the
    // full-exit rewind to the opacity-0 first frame — leaves a scene blank
    // while it's on screen but not yet centred (the "disappears then redraws a
    // few seconds later" on scroll-up). A generous band wakes it as soon as
    // it's prominently visible, so the blank window all but disappears.
    const center = new IntersectionObserver(
      ([entry]) => setLive(entry.isIntersecting),
      { rootMargin: '-20% 0px -20% 0px', threshold: 0 },
    );
    // Any part on screen → when it goes fully off, rewind for a clean re-entry.
    const full = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) rewind();
      },
      { threshold: 0 },
    );
    center.observe(el);
    full.observe(el);
    return () => {
      center.disconnect();
      full.disconnect();
    };
  }, []);
  return (
    <div
      ref={ref}
      className={`scene-anim group relative mx-auto w-full max-w-md${live ? ' inview' : ''}${paused ? ' paused' : ''}`}
    >
      {children}
      {hasAnim && (
        <div className="absolute bottom-2 right-2 z-10 flex gap-1 opacity-0 transition-opacity duration-200 group-hover:opacity-100 focus-within:opacity-100">
          <button
            type="button"
            aria-label={paused ? t('splash.play') : t('agentTools.pause')}
            onClick={() => setPaused((p) => !p)}
            className="grid h-7 w-7 place-items-center rounded-lg border border-white/15 bg-white/10 text-white/90 shadow-sm backdrop-blur-md transition hover:bg-white/20"
          >
            {paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
          </button>
          <button
            type="button"
            aria-label={t('splash.restart')}
            onClick={() => {
              rewind();
              setPaused(false);
            }}
            className="grid h-7 w-7 place-items-center rounded-lg border border-white/15 bg-white/10 text-white/90 shadow-sm backdrop-blur-md transition hover:bg-white/20"
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Collaborate beat: a shared-doc skeleton with live cursors that each emit a
 * reaction now and then, plus tap-to-react (a floating emote from the click,
 * like the whiteboard). Interactive but restrained.
 */
function CollabScene() {
  const idRef = useRef(0);
  const [bursts, setBursts] = useState<{ id: number; x: number; y: number; e: string }[]>([]);
  const onReact = (ev: React.MouseEvent<HTMLDivElement>) => {
    const r = ev.currentTarget.getBoundingClientRect();
    const id = (idRef.current += 1);
    const e = id % 2 ? '❤️' : '👍';
    setBursts((b) => [...b, { id, x: ev.clientX - r.left, y: ev.clientY - r.top, e }]);
    window.setTimeout(() => setBursts((b) => b.filter((x) => x.id !== id)), 1100);
  };
  return (
    <div
      onClick={onReact}
      className="relative mx-auto aspect-[4/3] w-full max-w-md cursor-pointer select-none overflow-hidden rounded-2xl border border-border/60 bg-card/50 backdrop-blur-sm"
    >
      {/* Sparse canvas skeleton, spread across the frame */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute left-6 top-6 h-2.5 w-28 rounded bg-border" />
        <div className="absolute left-6 top-11 h-2 w-16 rounded bg-border" />
        <div className="absolute right-7 top-8 h-16 w-24 rounded-lg border border-border" />
        <div className="absolute bottom-8 left-7 h-14 w-20 rounded-lg bg-border" />
        <div className="absolute bottom-9 right-8 h-2.5 w-24 rounded bg-border" />
        <div className="absolute bottom-5 right-8 h-2 w-16 rounded bg-border" />
      </div>
      {/* Live cursors — people + an agent — each dropping a reaction now and then */}
      <div className="cb-cur cb-you absolute">
        <MousePointer2 className="h-4 w-4 -rotate-12 fill-primary text-primary" />
        <span className="ml-1 rounded bg-primary px-1.5 py-0.5 text-[10px] font-medium text-primary-foreground">You</span>
        <span className="cb-react cb-react1" aria-hidden>❤️</span>
      </div>
      <div className="cb-cur cb-mia absolute">
        <MousePointer2 className="h-4 w-4 -rotate-12" style={{ color: '#3b82f6', fill: '#3b82f6' }} />
        <span className="ml-1 rounded px-1.5 py-0.5 text-[10px] font-medium text-white" style={{ background: '#3b82f6' }}>Alice</span>
        <span className="cb-react cb-react2" aria-hidden>👍</span>
      </div>
      <div className="cb-cur cb-masha absolute">
        <MousePointer2 className="h-4 w-4 -rotate-12" style={{ color: '#14b8a6', fill: '#14b8a6' }} />
        <span className="ml-1 rounded px-1.5 py-0.5 text-[10px] font-medium text-white" style={{ background: '#14b8a6' }}>Bob</span>
        <span className="cb-react cb-react3" aria-hidden>🎉</span>
      </div>
      <div className="cb-cur cb-agent absolute">
        <MousePointer2 className="h-4 w-4 -rotate-12" style={{ color: '#7f77dd', fill: '#7f77dd' }} />
        <span className="ml-1 rounded px-1.5 py-0.5 text-[10px] font-medium text-white" style={{ background: '#7f77dd' }}>agent</span>
      </div>
      {/* Tap-to-react bursts */}
      {bursts.map((b) => (
        <span key={b.id} className="cb-burst absolute" style={{ left: b.x, top: b.y }} aria-hidden>{b.e}</span>
      ))}
    </div>
  );
}

/**
 * Bring-your-own-AI beat: a speed race. {t('splash.mouseScreenshots')} grinds through a
 * screenshot -> click -> screenshot -> click loop (each step lights up with a
 * camera flash or a click ripple) while the Robutler command interface fires one
 * call straight to done. Pure-CSS motion so the scroll-in gate pauses/rewinds it
 * like every other beat.
 */
function ByoaScene() {
  const t = useT();
  const slow = ['shot', 'click', 'shot', 'click', 'shot', 'click', 'shot'];
  return (
    <div className="relative mx-auto flex aspect-[4/3] w-full max-w-md flex-col justify-center gap-8 overflow-hidden rounded-2xl border border-border/60 bg-card/50 px-6 py-6 backdrop-blur-sm">
      {/* Slow lane — screenshot, click, screenshot, click… The gray progress bar
          runs behind the steps and advances in lockstep with them, only ever
          inching toward a finish it never reaches. */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <span className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <MousePointer2 className="h-4 w-4" /> {t('splash.mouseScreenshots')}
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="relative inline-block h-4 w-[5.5rem] text-right">
              <span className="by-verb-a absolute inset-0">screenshot…</span>
              <span className="by-verb-b absolute inset-0">click…</span>
            </span>
            <span className="by-spin inline-block h-3 w-3 rounded-full border border-muted-foreground/40 border-t-muted-foreground" />
          </span>
        </div>
        <div className="relative flex items-center justify-between">
          <span className="absolute inset-x-0 top-1/2 h-2 -translate-y-1/2 rounded-full bg-border/40" />
          <span className="by-slowbar absolute left-0 top-1/2 h-2 -translate-y-1/2 rounded-full bg-muted-foreground/45" />
          {slow.map((kind, i) => {
            // The grind only ever reaches the first five steps before the cycle
            // resets; the last two never light (the work it never gets to). The
            // gray bar's leading edge tracks whichever of these is active.
            const active = i < 5;
            return (
              <span key={i} className={`relative z-10 grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-lg border border-border bg-card text-muted-foreground ${active ? 'by-step' : ''}`} style={active ? { animationDelay: `${i * 2.25}s` } : undefined}>
                {kind === 'shot' ? <Camera className="h-4 w-4" /> : <MousePointer2 className="h-4 w-4 -rotate-12" />}
                {active && (kind === 'shot'
                  ? <span className="by-flash absolute inset-0 bg-white" style={{ animationDelay: `${i * 2.25}s` }} />
                  : <span className="by-ripple absolute inset-0 rounded-lg border-2 border-primary" style={{ animationDelay: `${i * 2.25}s` }} />)}
              </span>
            );
          })}
        </div>
      </div>
      {/* Fast lane — one Robutler command, straight to done. */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <span className="inline-flex items-center gap-2 text-sm font-medium text-primary">
            <Zap className="h-4 w-4" /> {t('splash.robutlerCommands')}
          </span>
          <span className="by-done text-xs font-medium text-primary">done</span>
        </div>
        <div className="flex items-center gap-2.5">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary text-primary-foreground"><Zap className="h-4 w-4" /></span>
          <div className="relative h-2 flex-1 rounded-full bg-border/60">
            <span className="by-fastfill absolute inset-y-0 left-0 rounded-full bg-primary" />
          </div>
          <span className="by-donenode grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-primary text-primary"><Check className="h-4 w-4" /></span>
        </div>
      </div>
    </div>
  );
}

/** One beat, laid out per the active section-layout variant (q-row keys 0..9). */
function renderBeat(beat: Beat, i: number, variant: number) {
  const { eyebrow, title, sub } = beat;
  const visual = <AnimateInView>{beat.visual}</AnimateInView>;
  const odd = i % 2 === 1;
  const eb = <p className={EB}>{eyebrow}</p>;

  // 1 — full-bleed bands (alternating accent wash)
  if (variant === 1)
    return (
      <section key={i} className="py-16" style={{ background: odd ? 'color-mix(in oklab, var(--color-primary) 7%, transparent)' : 'transparent' }}>
        <div className="mx-auto grid max-w-5xl grid-cols-1 items-center gap-10 px-6 lg:grid-cols-2 lg:gap-16">
          <div className={odd ? 'lg:order-2' : ''}>{eb}<h2 className={`mt-3 ${H2}`}>{title}</h2><p className={`mt-4 max-w-sm ${SUB}`}>{sub}</p></div>
          <div className={odd ? 'lg:order-1' : ''}>{visual}</div>
        </div>
      </section>
    );
  // 2 — editorial numbered
  if (variant === 2)
    return (
      <section key={i} className="py-12 sm:py-20">
        <div className="mx-auto grid max-w-5xl grid-cols-1 items-center gap-10 px-6 lg:grid-cols-2 lg:gap-16">
          <div className={odd ? 'lg:order-2' : ''}>
            <span className="font-display text-5xl font-bold text-primary/30">{String(i + 1).padStart(2, '0')}</span>
            <h2 className={`mt-2 ${H2}`}>{title}</h2>
            <p className={`mt-3 max-w-sm ${SUB}`}>{sub}</p>
          </div>
          <div className={odd ? 'lg:order-1' : ''}>{visual}</div>
        </div>
      </section>
    );
  // 3 — all-left, oversized headings
  if (variant === 3)
    return (
      <section key={i} className="py-12 sm:py-20">
        <div className="mx-auto grid max-w-5xl grid-cols-1 items-center gap-10 px-6 text-left lg:grid-cols-2 lg:gap-16">
          <div>{eb}<h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-foreground sm:text-5xl">{title}</h2><p className={`mt-4 max-w-sm ${SUB}`}>{sub}</p></div>
          <div>{visual}</div>
        </div>
      </section>
    );
  // 4 — each beat in a glass card
  if (variant === 4)
    return (
      <section key={i} className="py-5 sm:py-7">
        <div className="mx-auto max-w-5xl px-6">
          <div className="grid grid-cols-1 items-center gap-8 rounded-3xl border border-border/60 bg-card/40 p-8 backdrop-blur-sm lg:grid-cols-2 lg:gap-12 lg:p-10">
            <div className={odd ? 'lg:order-2' : ''}>{eb}<h2 className={`mt-3 ${H2}`}>{title}</h2><p className={`mt-4 max-w-sm ${SUB}`}>{sub}</p></div>
            <div className={odd ? 'lg:order-1' : ''}>{visual}</div>
          </div>
        </div>
      </section>
    );
  // 5 — centered, visual stacked over text
  if (variant === 5)
    return (
      <section key={i} className="py-12 sm:py-20">
        <div className="mx-auto flex max-w-2xl flex-col items-center gap-8 px-6 text-center">
          <div className="w-full">{visual}</div>
          <div>{eb}<h2 className={`mt-3 ${H2}`}>{title}</h2><p className={`mx-auto mt-4 max-w-md ${SUB}`}>{sub}</p></div>
        </div>
      </section>
    );
  // 6 — split with accent panel
  if (variant === 6)
    return (
      <section key={i} className="py-6 sm:py-8">
        <div className="mx-auto grid max-w-5xl grid-cols-1 items-stretch gap-4 px-6 lg:grid-cols-2">
          <div className={`flex flex-col justify-center rounded-3xl p-8 lg:p-10 ${odd ? 'lg:order-2' : ''}`} style={{ background: 'color-mix(in oklab, var(--color-primary) 12%, transparent)' }}>
            {eb}<h2 className={`mt-3 ${H2}`}>{title}</h2><p className={`mt-4 ${SUB}`}>{sub}</p>
          </div>
          <div className={`flex items-center justify-center rounded-3xl border border-border/40 bg-card/30 p-6 ${odd ? 'lg:order-1' : ''}`}>{visual}</div>
        </div>
      </section>
    );
  // 7 — headline row over a centered visual
  if (variant === 7)
    return (
      <section key={i} className="py-10 sm:py-14">
        <div className="mx-auto max-w-5xl px-6">
          <div className="mb-6 flex flex-wrap items-baseline gap-x-4 gap-y-1">
            {eb}
            <h2 className={H2}>{title}</h2>
            <p className="text-base text-foreground/60">{sub}</p>
          </div>
          <div className="mx-auto max-w-2xl">{visual}</div>
        </div>
      </section>
    );
  // 8 — sticky visual, text scrolls beside
  if (variant === 8)
    return (
      <section key={i} className="py-12 sm:py-20">
        <div className="mx-auto grid max-w-5xl grid-cols-1 gap-10 px-6 lg:grid-cols-2 lg:gap-16">
          <div className={`lg:sticky lg:top-24 lg:self-start ${odd ? 'lg:order-2' : ''}`}>{visual}</div>
          <div className={`flex flex-col justify-center ${odd ? 'lg:order-1' : ''}`}>{eb}<h2 className="mt-3 font-display text-4xl font-semibold tracking-tight text-foreground sm:text-5xl">{title}</h2><p className={`mt-4 max-w-sm ${SUB}`}>{sub}</p></div>
        </div>
      </section>
    );
  // 9 — minimal / tight
  if (variant === 9)
    return (
      <section key={i} className="py-6">
        <div className="mx-auto grid max-w-4xl grid-cols-1 items-center gap-6 px-6 sm:grid-cols-2">
          <div className={odd ? 'sm:order-2' : ''}>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{eyebrow}</p>
            <h3 className="mt-1 font-display text-xl font-semibold text-foreground">{title}</h3>
            <p className="mt-1 text-sm text-foreground/60">{sub}</p>
          </div>
          <div className={odd ? 'sm:order-1' : ''}>{visual}</div>
        </div>
      </section>
    );
  // 0 — alternating two-column (default)
  return (
    <section key={i} className="py-12 sm:py-20">
      <div className="mx-auto grid max-w-5xl grid-cols-1 items-center gap-10 px-6 lg:grid-cols-2 lg:gap-16">
        <div className={odd ? 'lg:order-2' : ''}>{eb}<h2 className={`mt-3 ${H2}${beat.narrowTitle ? ' lg:max-w-[360px]' : ''}`}>{title}</h2><p className={`mt-4 max-w-sm ${SUB}`}>{sub}</p></div>
        <div className={odd ? 'lg:order-1' : ''}>{visual}</div>
      </div>
    </section>
  );
}

/** Robutler mark (light/dark swap). */
/** Only the links that actually go somewhere.
 *
 *  Legal docs render at two addresses: the legacy `/doc/<slug>` alias
 *  (app/(legal)/doc/[slug]) and the `/docs/<slug>` docs-site page built from the
 *  same markdown under docs/public. Only the `/docs/` form is enumerated into
 *  the sitemap (robutlerSource via /sitemap/docs.xml), so the alias is a
 *  duplicate nobody submits. This row is the only place the homepage links
 *  Terms and Privacy at all, and until 2026-08-22 it sent both to the alias.
 *  See seo-audit-2026-08-22 item 0.9. */
function LegalLinks({ className = '', muted = true }: { className?: string; muted?: boolean }) {
  const c = muted ? 'text-muted-foreground transition-colors hover:text-foreground' : 'opacity-80 transition-opacity hover:opacity-100';
  return (
    <div className={`flex flex-wrap items-center gap-x-5 gap-y-1.5 ${className}`}>
      {/* The only crawlable link from the homepage into the catalogue. Every
          app tile above points at `/?app=<id>`, which resolves to an ephemeral
          `/w/<uuid>` viewer that is deliberately noindex — so without this
          anchor the app and agent listing pages have no inbound link from any
          indexable page, and a crawler can reach them only via the sitemap.
          Sitemaps get URLs discovered; links are what rank them. */}
      <a href="/discover" className={c}>Browse apps</a>
      <a href="/docs" className={c}>Docs</a>
      <a href="/develop" className={c}>Develop</a>
      <a href="/docs/terms-of-service" target="_blank" rel="noopener noreferrer" className={c}>Terms</a>
      <a href="/docs/privacy-policy" target="_blank" rel="noopener noreferrer" className={c}>Privacy</a>
    </div>
  );
}

// Baked: the subtle glitch wordmark tuning (gx chromatic offset, two cycle speeds).
const GLITCH = { gx: '1.5px', gs: '3.6s', gs2: '4.2s' };
// Match the previous text wordmark's visual height (cap-height ≈ 0.72 × the old
// clamp(2.8rem, 9vw, 5.5rem) font-size); width follows the wordmark aspect ratio.
const FOOTER_MARK_H = 'clamp(1.5rem, 4.9vw, 2.96rem)';

/** Footer: the official ROBUTLER wordmark (SVG, masked) with the subtle glitch
 *  over a slim legal row. The chromatic-aberration glitch is kept by masking
 *  three color layers with the same wordmark glyphs (base + offset red/cyan). */
function renderFooter(t: (key: string, params?: Record<string, string | number>) => string) {
  return (
    <footer className="overflow-hidden pt-16 pb-8">
      <div className="mx-auto max-w-6xl px-6">
        <div
          className="ft-mark select-none"
          role="img"
          aria-label="Robutler"
          style={{ height: FOOTER_MARK_H, ['--gx' as string]: GLITCH.gx, ['--gs' as string]: GLITCH.gs, ['--gs2' as string]: GLITCH.gs2 } as React.CSSProperties}
        >
          <i aria-hidden />
        </div>
        <div className="mt-6 flex flex-col items-start gap-3 border-t border-border/40 pt-5 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <span>{t('splash.creatorEconomy')}</span>
          <LegalLinks />
        </div>
      </div>
    </footer>
  );
}

// The capsule rocket stays fixed (design 0); the 10 variants vary the blueprint
// "file" around it — helper sketch lines, guides, dimensions, frames.
// Starship spacecraft, drawn to the real silhouette (per SpaceX's outline): an
// ogive nosecone, two small forward flaps high on the body, two large swept aft
// flaps at the base, a sectioned fuselage, and engine nozzles at the bottom.
// Engineering-drawing feel (faint axis + tank-section seams) in our lime
// line-art style. Deliberately not a kid's rocket (no porthole / triangular
// fins / cartoon flames). viewBox 0 0 100 100, centred on x=50.
const CAPSULE = (
  <>
    {/* faint center axis */}
    <line x1="50" y1="8" x2="50" y2="83" strokeWidth="0.5" opacity="0.3" />
    {/* fuselage: ogive nosecone + cylindrical body */}
    <path d="M41 84 L 41 26 Q 41 12 50 6 Q 59 12 59 26 L 59 84 Z" strokeWidth="1.9" />
    {/* tank / section seams */}
    <line x1="41" y1="26" x2="59" y2="26" strokeWidth="0.65" opacity="0.45" />
    <line x1="41" y1="41" x2="59" y2="41" strokeWidth="0.6" opacity="0.38" />
    <line x1="41" y1="58" x2="59" y2="58" strokeWidth="0.65" opacity="0.42" />
    {/* forward flaps (upper, small) */}
    <path d="M41 30 L 31 34 L 33 49 L 41 47 Z" strokeWidth="1.65" />
    <path d="M59 30 L 69 34 L 67 49 L 59 47 Z" strokeWidth="1.65" />
    {/* aft flaps (lower, large, swept) */}
    <path d="M41 60 L 28 73 L 26 86 L 41 84 Z" strokeWidth="1.65" />
    <path d="M59 60 L 72 73 L 74 86 L 59 84 Z" strokeWidth="1.65" />
    {/* engine skirt + three Raptor nozzles (spec, not flames) */}
    <path d="M45 84 L 44 89 L 56 89 L 55 84" strokeWidth="1" opacity="0.65" />
    <line x1="47.5" y1="84" x2="47.5" y2="88.5" strokeWidth="0.9" opacity="0.6" />
    <line x1="50" y1="84" x2="50" y2="89" strokeWidth="0.9" opacity="0.6" />
    <line x1="52.5" y1="84" x2="52.5" y2="88.5" strokeWidth="0.9" opacity="0.6" />
  </>
);
// Baked: variant 4 (Crosshairs). shipDeco kept so the treatment is easy to swap.
function shipDeco(v: number) {
  switch (v) {
    case 1: // Frame — a drawing border
      return (
        <g strokeWidth="1.4" opacity="0.5">
          <rect x="6" y="6" width="88" height="88" rx="2" />
          <rect x="10.5" y="10.5" width="79" height="79" strokeWidth="0.7" />
        </g>
      );
    case 2: // Grid — graph paper
      return (
        <g strokeWidth="0.6" opacity="0.15">
          {[18, 31, 44, 57, 70, 83].map((y) => <line key={`h${y}`} x1="8" y1={y} x2="92" y2={y} />)}
          {[18, 31, 44, 57, 70, 83].map((x) => <line key={`v${x}`} x1={x} y1="8" x2={x} y2="92" />)}
        </g>
      );
    case 3: // Dimension lines
      return (
        <g strokeWidth="1" opacity="0.5">
          <line x1="18" y1="8" x2="18" y2="92" />
          <path d="M15 11 L 18 7 L 21 11" /><path d="M15 89 L 18 93 L 21 89" />
          <line x1="16" y1="8" x2="20" y2="8" /><line x1="16" y1="92" x2="20" y2="92" />
          <line x1="27" y1="96" x2="73" y2="96" />
          <path d="M30 93 L 26 96 L 30 99" /><path d="M70 93 L 74 96 L 70 99" />
        </g>
      );
    case 4: // Crosshair + corner registration marks
      return (
        <>
          <g strokeWidth="0.7" opacity="0.3"><line x1="50" y1="2" x2="50" y2="98" /><line x1="2" y1="45" x2="98" y2="45" /></g>
          <g strokeWidth="1" opacity="0.5">
            <line x1="8" y1="12" x2="16" y2="12" /><line x1="12" y1="8" x2="12" y2="16" />
            <line x1="84" y1="12" x2="92" y2="12" /><line x1="88" y1="8" x2="88" y2="16" />
            <line x1="8" y1="88" x2="16" y2="88" /><line x1="12" y1="84" x2="12" y2="92" />
            <line x1="84" y1="88" x2="92" y2="88" /><line x1="88" y1="84" x2="88" y2="92" />
          </g>
        </>
      );
    case 5: // Construction guide lines through key points
      return (
        <g strokeWidth="0.8" opacity="0.32">
          <line x1="50" y1="2" x2="50" y2="98" />
          <line x1="8" y1="7" x2="92" y2="7" /><line x1="8" y1="36" x2="92" y2="36" />
          <line x1="8" y1="45" x2="92" y2="45" /><line x1="8" y1="64" x2="92" y2="64" />
        </g>
      );
    case 6: // Frame + title block
      return (
        <g strokeWidth="1.2" opacity="0.5">
          <rect x="6" y="6" width="88" height="88" rx="2" />
          <rect x="60" y="79" width="30" height="11" />
          <line x1="60" y1="84.5" x2="90" y2="84.5" strokeWidth="0.7" />
          <line x1="75" y1="79" x2="75" y2="90" strokeWidth="0.7" />
        </g>
      );
    case 7: // Corner crop marks + a scale ruler
      return (
        <g strokeWidth="1.2" opacity="0.5">
          <path d="M6 14 L 6 6 L 14 6" /><path d="M86 6 L 94 6 L 94 14" />
          <path d="M6 86 L 6 94 L 14 94" /><path d="M86 94 L 94 94 L 94 86" />
          <line x1="20" y1="96" x2="80" y2="96" strokeWidth="0.7" />
          <g strokeWidth="0.7">{[20, 35, 50, 65, 80].map((x) => <line key={x} x1={x} y1="93" x2={x} y2="96" />)}</g>
        </g>
      );
    case 8: // Full schematic — grid + frame + a dimension
      return (
        <>
          <g strokeWidth="0.5" opacity="0.12">
            {[20, 33, 46, 59, 72].map((y) => <line key={`h${y}`} x1="8" y1={y} x2="92" y2={y} />)}
            {[20, 33, 46, 59, 72].map((x) => <line key={`v${x}`} x1={x} y1="8" x2={x} y2="92" />)}
          </g>
          <rect x="6" y="6" width="88" height="88" rx="2" strokeWidth="1.2" opacity="0.45" />
          <g strokeWidth="0.9" opacity="0.45">
            <line x1="16" y1="8" x2="16" y2="90" />
            <path d="M13 11 L 16 7 L 19 11" /><path d="M13 87 L 16 91 L 19 87" />
          </g>
        </>
      );
    case 9: // Targeting reticle
      return (
        <g strokeWidth="0.8" opacity="0.32">
          <circle cx="50" cy="46" r="40" />
          <circle cx="50" cy="46" r="27" strokeWidth="0.6" />
          <circle cx="50" cy="46" r="14" strokeWidth="0.5" />
          <line x1="50" y1="2" x2="50" y2="12" /><line x1="50" y1="80" x2="50" y2="90" />
          <line x1="6" y1="46" x2="16" y2="46" /><line x1="84" y1="46" x2="94" y2="46" />
        </g>
      );
    default:
      return null;
  }
}
function renderShip(v: number) {
  return (
    <svg
      className="ws-draw ws-ship"
      viewBox="0 0 100 100"
      preserveAspectRatio="xMidYMid meet"
      fill="none"
      stroke="var(--color-primary)"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {shipDeco(v)}
      {/* Scale the rocket down (~74%) about its centroid so it no longer
          dominates the frame; the blueprint deco stays full-size. */}
      <g transform="translate(50 47) scale(0.66) translate(-50 -47)">{CAPSULE}</g>
    </svg>
  );
}

// The text link of the FAQ: an answer's link, the fold's control and the quick
// start link under the list are one style, so the control reads as their peer.
const FAQ_LINK = 'font-medium text-primary underline-offset-4 transition-colors hover:underline';

/**
 * Short FAQ: the first FAQ_TOP questions, the rest folded under "See more FAQs".
 *
 * The control opens the rest IN PLACE and then reads "See less". Until
 * 2026-10-04 it was a link that left for the full FAQ in the docs, under a
 * list of all eleven rows.
 *
 * The folded rows are hidden, not unmounted. Every answer stays in the served
 * HTML, which is what a crawler reads, and a row the visitor opened under the
 * fold is still open when they unfold the list again.
 *
 * A component of its own so the fold's state re-renders this section only, not
 * the animated scenes above it. Exported for its test.
 */
export function FaqSection() {
  const t = useT();
  const [more, setMore] = useState(false);
  const moreId = useId();
  const row = (item: (typeof FAQ)[number]) => (
    <details key={item.q} className="group border-b border-border/40">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-5 font-display text-lg font-medium text-foreground [&::-webkit-details-marker]:hidden">
        {item.q}
        <span className="text-xl text-muted-foreground transition-transform duration-200 group-open:rotate-45">+</span>
      </summary>
      <p className="pb-5 text-base leading-relaxed text-foreground/70">
        {item.a}
        {item.link && (
          <>
            {' '}
            <a href={item.link.href} className={FAQ_LINK}>{item.link.text}</a>.
          </>
        )}
      </p>
    </details>
  );
  return (
    <section className="py-12 sm:py-20">
      <div className="mx-auto max-w-xl px-6">
        <h2 className="mb-8 text-center font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">{t('splash.commonQuestions')}</h2>
        <div className="border-t border-border/40">
          {FAQ.slice(0, FAQ_TOP).map(row)}
          <div id={moreId} hidden={!more}>
            {FAQ.slice(FAQ_TOP).map(row)}
          </div>
        </div>
        {/* The rest of the questions, and where a newcomer starts. */}
        <p className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-base">
          <button type="button" aria-expanded={more} aria-controls={moreId} onClick={() => setMore((v) => !v)} className={FAQ_LINK}>
            {more ? 'See less' : 'See more FAQs'}
          </button>
          <a href="/docs/guides/quick-start" className={FAQ_LINK}>New here? Start here.</a>
        </p>
      </div>
    </section>
  );
}

export function WelcomeSections({ variant = 0 }: { returning?: boolean; variant?: number }) {
  const t = useT();
  const beats: Beat[] = [
    {
      eyebrow: 'Pro apps',
      title: 'Use apps as simple or as powerful as you need.',
      sub: 'Here you find apps ranging from a snake game to a computer chip design software. Use it yourself or together with your AI.',
      visual: (
        <div className="ws-scene relative mx-auto aspect-[4/3] w-full max-w-md overflow-hidden rounded-2xl border border-border/60 bg-card/40 backdrop-blur-sm">
          {/* The whole workspace lives on a stage the camera rises over, then
              zooms app → app while a sketch is drawn inside each. */}
          <div className="ws-stage">
            <div className="ws-grid" />
            {/* left sidebar rail */}
            <div className="ws-rail">
              <span className="ws-railbtn" />
              <span className="ws-railbtn" />
              <span className="ws-railbtn" />
              <span className="ws-railbtn" />
            </div>
            {/* App 1 — a doodle */}
            <div className="ws-app ws-app1">
              <svg className="ws-draw ws-doodle" viewBox="0 0 120 80" preserveAspectRatio="xMidYMid meet" fill="none" aria-hidden>
                <path d="M16 64 q 13 -30 26 -9 t 24 -1" stroke="var(--color-primary)" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M98 11 L101.1 19.8 L110.4 20 L103 25.6 L105.6 34.5 L98 29.2 L90.4 34.5 L93 25.6 L85.6 20 L94.9 19.8 Z" stroke="var(--color-primary)" strokeWidth="3.5" strokeLinejoin="round" strokeLinecap="round" />
              </svg>
            </div>
            {/* App 2 — a chip */}
            <div className="ws-app ws-app2">
              <svg className="ws-draw ws-chip" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet" fill="none" aria-hidden>
                {/* QFP package: rounded body, centred die, pin-1 dot, 4 fine leads per side. */}
                <rect x="29" y="29" width="42" height="42" rx="7" stroke="var(--color-primary)" strokeWidth="2" />
                <rect x="42" y="42" width="16" height="16" rx="3" stroke="var(--color-primary)" strokeWidth="1.6" />
                <rect x="35" y="35" width="2.6" height="2.6" rx="1.3" stroke="var(--color-primary)" strokeWidth="1.5" />
                <g stroke="var(--color-primary)" strokeWidth="1.8" strokeLinecap="round">
                  <line x1="38" y1="29" x2="38" y2="21" /><line x1="46" y1="29" x2="46" y2="21" /><line x1="54" y1="29" x2="54" y2="21" /><line x1="62" y1="29" x2="62" y2="21" />
                  <line x1="38" y1="71" x2="38" y2="79" /><line x1="46" y1="71" x2="46" y2="79" /><line x1="54" y1="71" x2="54" y2="79" /><line x1="62" y1="71" x2="62" y2="79" />
                  <line x1="29" y1="38" x2="21" y2="38" /><line x1="29" y1="46" x2="21" y2="46" /><line x1="29" y1="54" x2="21" y2="54" /><line x1="29" y1="62" x2="21" y2="62" />
                  <line x1="71" y1="38" x2="79" y2="38" /><line x1="71" y1="46" x2="79" y2="46" /><line x1="71" y1="54" x2="79" y2="54" /><line x1="71" y1="62" x2="79" y2="62" />
                </g>
              </svg>
            </div>
            {/* App 3 — a starship blueprint (10 variants, switched with 0-9). */}
            <div className="ws-app ws-app3">
              {renderShip(4)}
            </div>
          </div>
          <div className="ws-caps">
            <span className="ws-cap ws-cap1">{t('splash.infiniteWorkspace')}</span>
            <span className="ws-cap ws-cap2">{t('splash.openAnApp')}</span>
            <span className="ws-cap ws-cap3">{t('splash.sketchDoodle')}</span>
            <span className="ws-cap ws-cap4">{t('splash.engineerChip')}</span>
            <span className="ws-cap ws-cap5">{t('splash.wholeSpacecraft')}</span>
          </div>
          {/* Finale: the workspace fades out and the question takes center stage,
              which also makes the loop back to the opening seamless. */}
          <div className="ws-finale"><span>{t('splash.whatWillYouBuild')}</span></div>
        </div>
      ),
    },
    {
      eyebrow: 'Bring your own AI',
      narrowTitle: true,
      title: 'Drive every app with the AI you already use.',
      sub: 'Connect the AI assistant or coding agent you already use, or your Robutler agent, then drive the app through a fast Robutler command interface, not slow, token-hungry computer-use emulation.',
      visual: <ByoaScene />,
    },
    {
      eyebrow: 'Customize apps',
      narrowTitle: true,
      title: "Don't build apps. Remix them.",
      sub: 'Building complex apps is hard, even with AI help. Remixing one is easy: open it and tell your AI agent what you want.',
      visual: (
        <div className="cm-scene relative mx-auto flex aspect-[4/3] w-full max-w-md flex-col overflow-hidden rounded-2xl border border-border/60 bg-card/70 text-left shadow-sm backdrop-blur-sm">
          <div className="flex items-center justify-between border-b border-border/50 px-4 py-2.5">
            <span className="text-xs text-muted-foreground">{t('splash.vectorDesignApp')}</span>
            <div className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-border" />
              <span className="h-2.5 w-2.5 rounded-full bg-border" />
              <span className="h-2.5 w-2.5 rounded-full bg-border" />
            </div>
          </div>
          <div className="cm-canvas relative grid flex-1 place-items-center p-4">
            {/* Vector-design canvas: a face illustration with vector anchor
                points around it, a magenta selected anchor + bezier handle on the
                right, and a cursor. */}
            <svg viewBox="0 0 157.362 142.929" className="h-32 w-auto opacity-95" xmlns="http://www.w3.org/2000/svg">
              <style>{`.byv-1{fill:#523bf4;stroke:#523bf4;stroke-width:2;stroke-linejoin:round}.byv-2{fill:#523bf4;opacity:.3}.byv-3{fill:#fff}.byv-4{fill:#e03df2;stroke:#e03df2;stroke-width:1.6;stroke-linejoin:round}`}</style>
              <circle className="byv-2" cx="69.671" cy="69.673" r="66.272" />
              <path className="byv-1" d="M84.586,90.959l-30.9-.024c-16.514-.57-29.827-14.268-29.827-30.875s13.312-30.306,29.827-30.875v-.017l.75-.009h30.15c17.038,0,30.899,13.862,30.899,30.9s-13.861,30.899-30.899,30.899ZM55.178,89.459h29.409c16.211,0,29.399-13.188,29.399-29.399s-13.188-29.4-29.399-29.4l-30.142.009c-16.037.169-29.084,13.354-29.084,29.392s13.047,29.222,29.084,29.392l.733.008Z" />
              <path className="byv-1" d="M54.395,56.075c2.272,0,4.121,1.849,4.121,4.121s-1.849,4.121-4.121,4.121-4.121-1.849-4.121-4.121,1.849-4.121,4.121-4.121M54.395,54.575c-3.104,0-5.621,2.516-5.621,5.621s2.516,5.621,5.621,5.621,5.621-2.516,5.621-5.621-2.516-5.621-5.621-5.621h0Z" />
              <path className="byv-1" d="M84.727,56.075c2.272,0,4.121,1.849,4.121,4.121s-1.849,4.121-4.121,4.121-4.121-1.849-4.121-4.121,1.849-4.121,4.121-4.121M84.727,54.575c-3.104,0-5.621,2.516-5.621,5.621s2.516,5.621,5.621,5.621,5.621-2.516,5.621-5.621-2.516-5.621-5.621-5.621h0Z" />
              <path className="byv-1" d="M69.671,136.695c-36.957,0-67.022-30.066-67.022-67.022S32.714,2.65,69.671,2.65s67.022,30.066,67.022,67.022-30.066,67.022-67.022,67.022ZM69.671,4.15C33.542,4.15,4.148,33.544,4.148,69.673s29.393,65.522,65.522,65.522,65.522-29.394,65.522-65.522S105.8,4.15,69.671,4.15Z" />
              <rect className="byv-4" x="133.854" y="67.677" width="5.323" height="5.603" />
              <path className="byv-4" d="M139.676,73.78h-6.322v-6.604h6.322v6.604ZM134.354,72.78h4.322v-4.604h-4.322v4.604Z" />
              <rect className="byv-3" x="65.583" y=".5" width="5.323" height="5.603" />
              <path className="byv-1" d="M71.406,6.604h-6.323V0h6.323v6.604ZM66.083,5.604h4.323V1h-4.323v4.604Z" />
              <rect className="byv-3" x="65.496" y="133.206" width="5.323" height="5.603" />
              <path className="byv-1" d="M71.319,139.309h-6.323v-6.603h6.323v6.603ZM65.996,138.309h4.323v-4.603h-4.323v4.603Z" />
              <rect className="byv-4" x="136.015" y="17.408" width="1" height="103.901" />
              <circle className="byv-4" cx="136.515" cy="17.327" r="1.774" />
              <path className="byv-4" d="M136.515,19.601c-1.254,0-2.274-1.02-2.274-2.273s1.021-2.274,2.274-2.274,2.274,1.021,2.274,2.274-1.021,2.273-2.274,2.273ZM136.515,16.053c-.703,0-1.274.571-1.274,1.274s.571,1.273,1.274,1.273,1.274-.571,1.274-1.273-.571-1.274-1.274-1.274Z" />
              <circle className="byv-4" cx="136.515" cy="121.63" r="1.774" />
              <path className="byv-4" d="M136.515,123.904c-1.254,0-2.274-1.021-2.274-2.274s1.021-2.274,2.274-2.274,2.274,1.021,2.274,2.274-1.021,2.274-2.274,2.274ZM136.515,120.355c-.703,0-1.274.571-1.274,1.274s.571,1.274,1.274,1.274,1.274-.571,1.274-1.274-.571-1.274-1.274-1.274Z" />
              <path className="byv-3" d="M150.607,135.614c-.15.064-.413.206-.467.332l-.888,2.067-1.865,4.326c-.16.372-.473.586-.868.59-.338.003-.748-.199-.876-.56l-.959-2.71-4.408-12.609c-.112-.32.016-.695.217-.907.159-.168.53-.418.846-.308l15.485,5.386c.355.124.504.488.535.793s-.092.737-.404.873l-1.229.539-2.958,1.26-2.16.927ZM148.956,135.187c.302-.419.61-.64,1.045-.826l4.805-2.051c.132-.057.255-.102.221-.252l-2.274-.787c-3.128-1.082-10.792-3.752-10.792-3.752,0,0,3.259,8.541,4.482,12.948.01.035.096.14.126.112l.109-.101,2.278-5.292Z" />
              <rect className="byv-3" x=".5" y="67.019" width="5.323" height="5.603" />
              <path className="byv-1" d="M6.323,73.122H0v-6.603h6.323v6.603ZM1,72.122h4.323v-4.603H1v4.603Z" />
            </svg>
            {/* The added control: a theme switch that pops in (dark), flips to
                light, then back to dark. */}
            <div className="cm-toggle absolute right-3 top-3 inline-flex items-center gap-2 rounded-full bg-black/80 px-2.5 py-1.5 text-white shadow-lg shadow-black/40 ring-1 ring-white/30 backdrop-blur">
              <Sun className="h-3.5 w-3.5 text-amber-300" />
              <span className="cm-track relative inline-block h-4 w-7 rounded-full bg-white/25">
                <span className="cm-knob absolute left-0.5 top-0.5 h-3 w-3 rounded-full bg-white shadow" />
              </span>
              <Moon className="h-3.5 w-3.5 text-slate-200" />
            </div>
          </div>
          <div className="flex items-center gap-2 border-t border-border/50 px-4 py-3">
            <Sparkles className="h-4 w-4 shrink-0 text-primary" />
            <span className="inline-flex items-center font-mono text-sm text-foreground" style={{ ['--w' as string]: '25.5ch' }}>
              <span className="cm-type">{t('splash.addThemeSwitch')}</span>
              <i className="rm-caret" />
            </span>
          </div>
        </div>
      ),
    },
    {
      eyebrow: 'Collaborate',
      narrowTitle: true,
      title: 'Humans and agents, one canvas.',
      sub: 'Every app is multiplayer by default. Work in real time with people and AI agents, and your AI is plugged into a whole network of other agents it can pull in to help.',
      visual: <CollabScene />,
    },
    {
      eyebrow: 'Earn',
      title: 'Publish once. Earn from real usage.',
      sub: "Publish an app or an agent. When people or their agents use it, even after it's remixed, you can earn Creator Rewards. Cash-out is coming soon.",
      visual: (
        <div className="mx-auto w-full max-w-md space-y-2 rounded-2xl border border-border/60 bg-card/50 p-3 backdrop-blur-sm">
          {FEED.map((row) => (
            <div key={row.name} className="flex items-center gap-3 rounded-xl border border-border/50 bg-card/60 px-3 py-2.5">
              <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-[11px] font-medium text-white" style={{ background: row.color }}>
                {row.initial}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-medium text-foreground">{row.name}</p>
                <p className="truncate text-[11px] text-muted-foreground">{row.by}</p>
              </div>
              <span className="shrink-0 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-medium text-primary ring-1 ring-primary/30">{row.badge}</span>
            </div>
          ))}
        </div>
      ),
    },
  ];

  return (
    <div className="relative z-10 w-full pt-16 sm:pt-32">
      <style jsx global>{`
        /* Footer ROBUTLER wordmark: glitch family, tuned per variant via --gx/--gs. */
        .ft-word { margin-left: -0.06em; }
        .ft-glitch { position: relative; --gx: 2.5px; --gs: 2.6s; --gs2: 3.2s; }
        .ft-glitch::before, .ft-glitch::after { content: attr(data-text); position: absolute; left: 0; top: 0; width: 100%; z-index: -1; }
        .ft-glitch::before { color: #ff0044; transform: translate(calc(var(--gx) * -1), 0); animation: ft-ga var(--gs) infinite steps(2, end) alternate; }
        .ft-glitch::after { color: #00e5ff; transform: translate(var(--gx), 0); animation: ft-gb var(--gs2) infinite steps(2, end) alternate; }
        @keyframes ft-ga { 0% { transform: translate(0,0) } 25% { transform: translate(calc(var(--gx) * -1), 1px) } 50% { transform: translate(var(--gx), -1px) } 75% { transform: translate(calc(var(--gx) * -1), 0) } 100% { transform: translate(var(--gx), 1px) } }
        @keyframes ft-gb { 0% { transform: translate(0,0) } 25% { transform: translate(var(--gx), -1px) } 50% { transform: translate(calc(var(--gx) * -1), 1px) } 75% { transform: translate(var(--gx), 0) } 100% { transform: translate(calc(var(--gx) * -1), -1px) } }
        .ft-glitch.ft-static::before, .ft-glitch.ft-static::after { animation: none; }
        .ft-glitch.ft-flicker::before { animation: ft-ga var(--gs) infinite steps(2, end) alternate, ft-flick 4s steps(1, end) infinite; }
        .ft-glitch.ft-flicker::after { animation: ft-gb var(--gs2) infinite steps(2, end) alternate, ft-flick 5.5s steps(1, end) infinite; }
        @keyframes ft-flick { 0%, 6%, 9%, 52%, 55%, 100% { opacity: 1 } 7%, 8% { opacity: 0 } 53%, 54% { opacity: 0.35 } }
        .ft-glitch.ft-slice::before { animation: ft-ga var(--gs) infinite steps(2, end) alternate, ft-slice-a 3.3s steps(1, end) infinite; }
        .ft-glitch.ft-slice::after { animation: ft-gb var(--gs2) infinite steps(2, end) alternate, ft-slice-b 2.7s steps(1, end) infinite; }
        @keyframes ft-slice-a { 0%, 100% { clip-path: inset(0 0 0 0) } 20% { clip-path: inset(8% 0 78% 0) } 40% { clip-path: inset(62% 0 18% 0) } 60% { clip-path: inset(35% 0 45% 0) } 80% { clip-path: inset(82% 0 4% 0) } }
        @keyframes ft-slice-b { 0%, 100% { clip-path: inset(0 0 0 0) } 25% { clip-path: inset(72% 0 10% 0) } 50% { clip-path: inset(18% 0 66% 0) } 75% { clip-path: inset(48% 0 32% 0) } }
        @media (prefers-reduced-motion: reduce) {
          .ft-glitch::before, .ft-glitch::after { animation: none !important; display: none; }
        }
        /* Official ROBUTLER wordmark (SVG) carrying the same chromatic glitch:
           three layers masked by the same glyphs — base (foreground) plus an
           offset red + cyan copy behind it. Height set inline; width follows the
           wordmark's intrinsic aspect ratio via 'contain'. */
        .ft-mark { position: relative; display: block; aspect-ratio: 115.605 / 14; }
        .ft-mark > i, .ft-mark::before, .ft-mark::after {
          position: absolute; inset: 0;
          -webkit-mask: url('/icons/Wm_W.svg') left center / contain no-repeat;
          mask: url('/icons/Wm_W.svg') left center / contain no-repeat;
        }
        .ft-mark::before, .ft-mark::after { content: ''; }
        /* Base glyphs paint on top (z 2); the offset red/cyan copies sit behind
           (z 1) and peek out at the edges as the chromatic glitch. */
        .ft-mark > i { background-color: var(--color-foreground); z-index: 2; }
        .ft-mark::before { background-color: #ff0044; z-index: 1; animation: ft-ga var(--gs) infinite steps(2, end) alternate; }
        .ft-mark::after { background-color: #00e5ff; z-index: 1; animation: ft-gb var(--gs2) infinite steps(2, end) alternate; }
        @media (prefers-reduced-motion: reduce) {
          .ft-mark::before, .ft-mark::after { animation: none !important; display: none; }
        }
        /* Scroll-in gate: every beat visual stays frozen at frame 0 until it
           is prominently in view, so it always opens from the beginning. */
        .scene-anim, .scene-anim * { animation-play-state: paused !important; }
        .scene-anim.inview, .scene-anim.inview * { animation-play-state: running !important; }
        .scene-anim.paused, .scene-anim.paused * { animation-play-state: paused !important; }
        .rm-type {
          display: inline-block; overflow: hidden; white-space: nowrap;
          vertical-align: bottom; width: 0; animation: rm-type 6s ease-in-out infinite;
        }
        @keyframes rm-type { 0% { width: 0 } 30% { width: var(--w) } 90% { width: var(--w) } 100% { width: 0 } }
        .rm-caret {
          display: inline-block; width: 2px; height: 1.1em; margin-left: 2px;
          background: currentColor; animation: rm-blink 1s steps(1) infinite;
        }
        @keyframes rm-blink { 0%, 50% { opacity: 1 } 51%, 100% { opacity: 0 } }
        .rm-feature { animation: rm-pop 6s ease-in-out infinite; }
        @keyframes rm-pop {
          0%, 30% { opacity: 0; transform: scale(0.85) }
          38% { opacity: 1; transform: scale(1.08) }
          46%, 88% { opacity: 1; transform: scale(1) }
          100% { opacity: 0; transform: scale(0.85) }
        }
        .rg-on { opacity: 0; animation: rg-on 5s ease-in-out infinite; }
        @keyframes rg-on { 0%, 8% { opacity: 0 } 16% { opacity: 1 } 32% { opacity: 0 } 100% { opacity: 0 } }
        .rg-chip { animation: rg-glow 5s ease-in-out infinite; }
        @keyframes rg-glow {
          0%, 58% { box-shadow: 0 0 0 0 transparent }
          74% { box-shadow: 0 0 26px 2px color-mix(in oklab, var(--color-primary) 55%, transparent) }
          100% { box-shadow: 0 0 0 0 transparent }
        }
        .cb-cur { display: inline-flex; align-items: center; }
        .cb-you { animation: cb-you 15s ease-in-out infinite; }
        @keyframes cb-you {
          0% { left: 10%; top: 60% } 25% { left: 48%; top: 24% } 50% { left: 62%; top: 62% }
          75% { left: 24%; top: 38% } 100% { left: 10%; top: 60% }
        }
        .cb-mia { animation: cb-mia 17s ease-in-out infinite; }
        @keyframes cb-mia {
          0% { left: 64%; top: 58% } 25% { left: 30%; top: 30% } 50% { left: 70%; top: 30% }
          75% { left: 46%; top: 64% } 100% { left: 64%; top: 58% }
        }
        .cb-masha { animation: cb-masha 19s ease-in-out infinite; }
        @keyframes cb-masha {
          0% { left: 30%; top: 72% } 25% { left: 70%; top: 48% } 50% { left: 42%; top: 28% }
          75% { left: 66%; top: 68% } 100% { left: 30%; top: 72% }
        }
        .cb-agent { animation: cb-agent 14s ease-in-out infinite; }
        @keyframes cb-agent {
          0% { left: 72%; top: 24% } 25% { left: 38%; top: 54% } 50% { left: 18%; top: 30% }
          75% { left: 58%; top: 58% } 100% { left: 72%; top: 24% }
        }
        /* Reactions emit from each cursor (positioned relative to it), float up + fade. */
        .cb-react { position: absolute; left: 8px; top: -6px; font-size: 18px; line-height: 1; opacity: 0; pointer-events: none; filter: drop-shadow(0 2px 5px rgba(0,0,0,.5)); }
        .cb-react1 { animation: cb-react 7s ease-out infinite 1.2s; }
        .cb-react2 { animation: cb-react 7s ease-out infinite 4s; }
        @keyframes cb-react {
          0% { opacity: 0; transform: translateY(2px) scale(.4) }
          5% { opacity: 1; transform: translateY(-8px) scale(1.1) }
          9% { transform: translateY(-14px) scale(1) }
          20% { opacity: 1; transform: translateY(-32px) scale(1) }
          28%, 100% { opacity: 0; transform: translateY(-46px) scale(.85) }
        }
        /* Tap-to-react burst (spawned at the click point, one-shot). */
        .cb-burst { font-size: 22px; line-height: 1; pointer-events: none; filter: drop-shadow(0 2px 5px rgba(0,0,0,.5)); animation: cb-burst 1.1s ease-out forwards; }
        @keyframes cb-burst {
          0% { opacity: 0; transform: translate(-50%,-40%) scale(.4) }
          15% { opacity: 1; transform: translate(-50%,-90%) scale(1.2) }
          100% { opacity: 0; transform: translate(-50%,-240%) scale(1) }
        }
        /* Pro-apps scene: rise over the workspace → zoom app→app, sketching a doodle, a chip, a spaceship → "what will you build?" */
        .ws-stage { position: absolute; inset: 0; transform-origin: 0 0; opacity: 0; animation: ws-pan 16s cubic-bezier(.66,0,.2,1) infinite; }
        @keyframes ws-pan {
          0% { opacity: 0; transform: translate(0%,0%) scale(1.14) }
          5%, 24% { opacity: 1; transform: translate(0%,0%) scale(1) }
          28%, 40% { opacity: 1; transform: translate(-42.3%,-44.9%) scale(2.6) }
          44%, 56% { opacity: 1; transform: translate(-135.9%,-34.5%) scale(2.6) }
          60%, 78% { opacity: 1; transform: translate(-83.9%,-128.1%) scale(2.6) }
          86%, 100% { opacity: 0; transform: translate(0%,0%) scale(1) }
        }
        /* Centered finale; fades in as the stage fades out, then loops cleanly. */
        .ws-finale { position: absolute; inset: 0; display: grid; place-items: center; padding: 0 1.5rem; text-align: center; opacity: 0; z-index: 3; pointer-events: none; animation: ws-finale 16s ease infinite; }
        .ws-finale span { font-size: clamp(20px, 6vw, 28px); font-weight: 800; letter-spacing: -0.02em; color: var(--color-foreground); text-shadow: 0 2px 18px var(--color-background); }
        @keyframes ws-finale { 0%,84% { opacity: 0; transform: scale(.92) } 92%,99% { opacity: 1; transform: scale(1) } 100% { opacity: 0; transform: scale(1.03) } }
        .ws-grid {
          position: absolute; inset: -10%;
          background-image:
            linear-gradient(color-mix(in oklab, var(--color-primary) 18%, transparent) 1px, transparent 1px),
            linear-gradient(90deg, color-mix(in oklab, var(--color-primary) 18%, transparent) 1px, transparent 1px);
          background-size: 26px 26px;
          -webkit-mask-image: radial-gradient(ellipse 85% 85% at center, #000 40%, transparent 92%);
          mask-image: radial-gradient(ellipse 85% 85% at center, #000 40%, transparent 92%);
          animation: ws-drift 5s linear infinite;
        }
        @keyframes ws-drift { from { background-position: 0 0 } to { background-position: 26px 26px } }
        .ws-rail {
          position: absolute; left: 0; top: 0; bottom: 0; width: 14%;
          display: flex; flex-direction: column; align-items: center; gap: 7px; padding-top: 11px;
          background: color-mix(in oklab, var(--color-card) 75%, transparent);
          border-right: 1px solid var(--color-border);
        }
        .ws-railbtn { width: 54%; aspect-ratio: 1; border-radius: 5px; background: color-mix(in oklab, var(--color-foreground) 16%, transparent); }
        .ws-railbtn:first-child { background: var(--color-primary); }
        .ws-app {
          position: absolute; width: 23%; height: 25%; border-radius: 10px; opacity: 0; overflow: hidden;
          background: var(--color-card); border: 1px solid var(--color-border);
          box-shadow: 0 10px 26px -8px rgba(0,0,0,.75);
          animation: ws-apppop 16s cubic-bezier(.34,1.56,.64,1) infinite;
        }
        .ws-app::before { content: ''; position: absolute; inset: 0 0 auto 0; height: 16%; }
        @keyframes ws-apppop {
          0%, 12% { opacity: 0; transform: scale(.2) translateY(22px) }
          18% { opacity: 1; transform: scale(1.12) translateY(0) }
          22%, 100% { opacity: 1; transform: scale(1) translateY(0) }
        }
        .ws-app1 { left: 24%; top: 24%; }
        .ws-app1::before { background: linear-gradient(90deg,#7c6cf0,#ec4899); }
        .ws-app2 { left: 60%; top: 20%; }
        .ws-app2::before { background: linear-gradient(90deg,#38bdf8,#34d399); }
        .ws-app3 { left: 40%; top: 56%; }
        .ws-app3::before { background: linear-gradient(90deg,#fbbf24,#fb923c); }
        /* Explicit width/height (not a 4-sided inset) so the <svg> — a replaced
           element — gets a DEFINITE box. Safari/WebKit otherwise ignores the
           right/bottom offsets and falls back to the SVG's intrinsic size
           anchored top-left, so the icon renders off-centre (Chrome resolves
           the box from the offsets and centres via preserveAspectRatio). The
           box sits in the card's content area (below the 16% title bar) and is
           vertically centred there: top 18% + 76% tall → centre ≈ 56%. */
        .ws-draw { position: absolute; left: 11%; top: 18%; width: 78%; height: 76%; opacity: 0; }
        .ws-draw path, .ws-draw rect, .ws-draw circle, .ws-draw line { stroke-dasharray: 360; stroke-dashoffset: 360; }
        /* App 1 doodle, drawn while the camera holds on it */
        .ws-doodle { animation: ws-fade1 16s ease infinite; }
        @keyframes ws-fade1 { 0%,27% { opacity: 0 } 29%,40% { opacity: 1 } 43%,100% { opacity: 0 } }
        .ws-doodle path { animation: ws-stroke1 16s ease-out infinite; }
        @keyframes ws-stroke1 { 0%,29% { stroke-dashoffset: 360 } 38% { stroke-dashoffset: 0 } 43%,100% { stroke-dashoffset: 360 } }
        /* App 2 chip */
        .ws-chip { animation: ws-fade2 16s ease infinite; }
        @keyframes ws-fade2 { 0%,43% { opacity: 0 } 45%,56% { opacity: 1 } 59%,100% { opacity: 0 } }
        .ws-chip rect, .ws-chip line { animation: ws-stroke2 16s ease-out infinite; }
        @keyframes ws-stroke2 { 0%,45% { stroke-dashoffset: 360 } 54% { stroke-dashoffset: 0 } 59%,100% { stroke-dashoffset: 360 } }
        /* App 3 spaceship */
        .ws-ship { animation: ws-fade3 16s ease infinite; }
        @keyframes ws-fade3 { 0%,59% { opacity: 0 } 61%,82% { opacity: 1 } 88%,100% { opacity: 0 } }
        .ws-ship path, .ws-ship circle, .ws-ship line, .ws-ship rect { animation: ws-stroke3 16s ease-out infinite; }
        @keyframes ws-stroke3 { 0%,61% { stroke-dashoffset: 360 } 70%,100% { stroke-dashoffset: 0 } }
        /* Punchy captions */
        .ws-caps { position: absolute; left: 0; right: 0; bottom: 14px; height: 1.4em; text-align: center; z-index: 2; }
        .ws-cap { position: absolute; left: 0; right: 0; font-size: clamp(16px, 4.4vw, 19px); font-weight: 800; letter-spacing: -0.01em; color: var(--color-foreground); text-shadow: 0 2px 14px var(--color-background); opacity: 0; transform: translateY(12px) scale(.9); }
        .ws-cap1 { animation: ws-cap1 16s cubic-bezier(.34,1.56,.64,1) infinite } @keyframes ws-cap1 { 0%{opacity:0;transform:translateY(12px) scale(.9)} 3%,9%{opacity:1;transform:translateY(0) scale(1)} 11%,100%{opacity:0;transform:translateY(-8px) scale(.98)} }
        .ws-cap2 { animation: ws-cap2 16s cubic-bezier(.34,1.56,.64,1) infinite } @keyframes ws-cap2 { 0%,11%{opacity:0;transform:translateY(12px) scale(.9)} 13%,22%{opacity:1;transform:translateY(0) scale(1)} 26%,100%{opacity:0;transform:translateY(-8px) scale(.98)} }
        .ws-cap3 { animation: ws-cap3 16s cubic-bezier(.34,1.56,.64,1) infinite } @keyframes ws-cap3 { 0%,28%{opacity:0;transform:translateY(12px) scale(.9)} 30%,40%{opacity:1;transform:translateY(0) scale(1)} 43%,100%{opacity:0;transform:translateY(-8px) scale(.98)} }
        .ws-cap4 { animation: ws-cap4 16s cubic-bezier(.34,1.56,.64,1) infinite } @keyframes ws-cap4 { 0%,44%{opacity:0;transform:translateY(12px) scale(.9)} 46%,56%{opacity:1;transform:translateY(0) scale(1)} 59%,100%{opacity:0;transform:translateY(-8px) scale(.98)} }
        .ws-cap5 { animation: ws-cap5 16s cubic-bezier(.34,1.56,.64,1) infinite } @keyframes ws-cap5 { 0%,60%{opacity:0;transform:translateY(12px) scale(.9)} 62%,78%{opacity:1;transform:translateY(0) scale(1)} 82%,100%{opacity:0;transform:translateY(-8px) scale(.98)} }
        /* Speed race (10s loop, everything on it so the whole thing resets in
           lockstep). Slow lane: the grind lights only the first five of the seven
           steps in sequence (i * 2.25s delay, step 4 at 9s = 90%) — camera flash
           on screenshots, click ripple on clicks — while the gray bar tracks the
           active step to ~70%, never reaching the last two, then resets. The fast
           lane's Robutler command is done by ~9% and then just waits. */
        .by-fastfill { animation: by-fastfill 10s cubic-bezier(.22,.61,.36,1) infinite; }
        @keyframes by-fastfill { 0% { width: 0 } 9% { width: 100% } 13%, 100% { width: 100% } }
        .by-donenode { opacity: .35; animation: by-donenode 10s ease infinite; }
        @keyframes by-donenode { 0%, 9% { opacity: .35; transform: scale(1) } 13% { opacity: 1; transform: scale(1.14) } 18%, 94% { opacity: 1; transform: scale(1) } 98%, 100% { opacity: .35; transform: scale(1) } }
        .by-done { opacity: 0; animation: by-done 10s ease infinite; }
        @keyframes by-done { 0%, 10% { opacity: 0 } 14% { opacity: 1 } 94% { opacity: 1 } 98%, 100% { opacity: 0 } }
        .by-step { animation: by-step 10s ease-in-out infinite; }
        @keyframes by-step {
          0% { border-color: var(--color-border); color: var(--color-muted-foreground); transform: scale(1) }
          4% { border-color: color-mix(in oklab, var(--color-foreground) 55%, transparent); color: var(--color-foreground); transform: scale(1.14) }
          9%, 100% { border-color: var(--color-border); color: var(--color-muted-foreground); transform: scale(1) }
        }
        .by-flash { opacity: 0; animation: by-flash 10s ease-out infinite; }
        @keyframes by-flash { 0% { opacity: 0 } 2.5% { opacity: .85 } 8% { opacity: 0 } 100% { opacity: 0 } }
        .by-ripple { opacity: 0; animation: by-ripple 10s ease-out infinite; }
        @keyframes by-ripple { 0% { opacity: 0; transform: scale(.55) } 3% { opacity: .9; transform: scale(1) } 11% { opacity: 0; transform: scale(1.7) } 100% { opacity: 0 } }
        .by-spin { animation: by-spin .9s linear infinite; }
        @keyframes by-spin { to { transform: rotate(360deg) } }
        /* Slow lane's gray bar's leading edge sits under the active step: 5% ->
           65% maps to step 0 -> step 4 over 0 -> 90% (each step at i * 22.5%),
           then it inches to 70% and resets — always just short of the last two
           steps it never reaches. */
        .by-slowbar { width: 5%; animation: by-slowbar 10s linear infinite; }
        @keyframes by-slowbar { 0% { width: 5% } 90% { width: 65% } 100% { width: 70% } }
        /* Status verb alternates screenshot… / click… on the SAME 10s loop as the
           steps, so it never drifts. Each active step owns ~22.5% (2.25s); the
           click steps (1, 3) sit in the 22.5-45 / 67.5-90 bands. */
        .by-verb-a { animation: by-verb-a 10s linear infinite; }
        @keyframes by-verb-a { 0%, 21% { opacity: 1 } 24%, 44% { opacity: 0 } 46%, 66% { opacity: 1 } 69%, 89% { opacity: 0 } 91%, 100% { opacity: 1 } }
        .by-verb-b { animation: by-verb-b 10s linear infinite; }
        @keyframes by-verb-b { 0%, 21% { opacity: 0 } 24%, 44% { opacity: 1 } 46%, 66% { opacity: 0 } 69%, 89% { opacity: 1 } 91%, 100% { opacity: 0 } }
        /* Customize: type the prompt, a theme switch pops in (dark), flips to
           light, then back to dark, then holds. One 11s timeline; starts dark.
           The light theme is a soft, low-contrast grey — easy on the eyes. */
        .cm-type {
          display: inline-block; overflow: hidden; white-space: nowrap;
          vertical-align: bottom; width: 0; animation: cm-type 13s steps(25, end) infinite;
        }
        @keyframes cm-type { 0%,3% { width: 0 } 14%,90% { width: var(--w) } 94%,100% { width: 0 } }
        .cm-canvas { background-color: #15151c; animation: cm-bg 13s ease infinite; }
        @keyframes cm-bg { 0%,32% { background-color: #15151c } 35%,49% { background-color: #b6b4c0 } 52%,63% { background-color: #15151c } 66%,79% { background-color: #b6b4c0 } 82%,100% { background-color: #15151c } }
        .cm-toggle { opacity: 0; animation: cm-pop 13s cubic-bezier(.34,1.56,.64,1) infinite; }
        @keyframes cm-pop {
          0%, 16% { opacity: 0; transform: scale(.8); filter: drop-shadow(0 0 0 rgba(207,224,74,0)) }
          22% { opacity: 1; transform: scale(1.14); filter: drop-shadow(0 0 8px rgba(207,224,74,.85)) }
          28%, 92% { opacity: 1; transform: scale(1); filter: drop-shadow(0 0 0 rgba(207,224,74,0)) }
          96%, 100% { opacity: 0; transform: scale(.8); filter: drop-shadow(0 0 0 rgba(207,224,74,0)) }
        }
        .cm-knob { animation: cm-knob 13s ease infinite; }
        @keyframes cm-knob { 0%,32% { transform: translateX(12px) } 35%,49% { transform: translateX(0) } 52%,63% { transform: translateX(12px) } 66%,79% { transform: translateX(0) } 82%,100% { transform: translateX(12px) } }

        @media (prefers-reduced-motion: reduce) {
          .rm-caret, .rg-on, .rg-chip, .cb-you, .cb-mia, .cb-agent, .cb-react, .cb-burst,
          .ws-stage, .ws-grid, .ws-app, .ws-draw, .ws-doodle path, .ws-chip rect, .ws-chip line, .ws-ship path, .ws-ship circle, .ws-ship line, .ws-ship rect, .ws-cap, .ws-finale,
          .cm-type, .cm-canvas, .cm-toggle, .cm-knob {
            animation: none !important;
          }
          .cm-type { width: var(--w) }
          .rg-on, .cb-react1, .cm-toggle { opacity: 1 }
          .ws-stage { opacity: 1; transform: none }
          .ws-app1, .ws-app2, .ws-app3 { opacity: 1; transform: none }
          .ws-cap1 { opacity: 1; transform: none }
          .cm-canvas { background-color: #15151c }
          .cm-knob { transform: translateX(12px) }
        }
      `}</style>

      {beats.map((b, i) => renderBeat(b, i, variant))}

      <FaqSection />

      {renderFooter(t)}
    </div>
  );
}
